import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ListFilter, Search } from 'lucide-react';
import { BoardSearchPickerShell } from '../../board/board-search-filters/boardSearchPickerShell';

function filterTecGroups(groups, searchTerm) {
  const q = String(searchTerm || '').trim().toLowerCase();
  if (!q) return groups;
  return groups
    .map(({ grupo, itens }) => {
      const grupoMatch = String(grupo).toLowerCase().includes(q);
      const filteredItens = grupoMatch
        ? itens
        : itens.filter((nome) => String(nome).toLowerCase().includes(q));
      if (filteredItens.length === 0) return null;
      return { grupo, itens: filteredItens };
    })
    .filter(Boolean);
}

export function DeqEquipTecnologiaFilterPanel({
  groups = [],
  searchTerm = '',
  onSearchChange,
  selectedTecs = [],
  onToggleTec,
  onToggleGrupo,
  onSetTecs,
}) {
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const bulkAnchorRef = useRef(null);
  const selectedSet = useMemo(() => new Set(selectedTecs), [selectedTecs]);

  const filteredGroups = useMemo(() => filterTecGroups(groups, searchTerm), [groups, searchTerm]);

  const visibleTecs = useMemo(
    () => filteredGroups.flatMap((g) => g.itens),
    [filteredGroups]
  );

  const allVisibleSelected =
    visibleTecs.length > 0 && visibleTecs.every((nome) => selectedSet.has(nome));

  useEffect(() => {
    if (!bulkMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (bulkAnchorRef.current?.contains(event.target)) return;
      setBulkMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [bulkMenuOpen]);

  const handleBulkToggle = () => {
    if (!onSetTecs || visibleTecs.length === 0) return;
    const next = allVisibleSelected
      ? selectedTecs.filter((t) => !visibleTecs.includes(t))
      : Array.from(new Set([...selectedTecs, ...visibleTecs]));
    onSetTecs(next);
    setBulkMenuOpen(false);
  };

  return (
    <BoardSearchPickerShell>
      <div className="bsf-simple-picker-search">
        <Search size={13} strokeWidth={2} className="bsf-simple-picker-search-icon" aria-hidden />
        <input
          type="text"
          className="bsf-simple-picker-input"
          placeholder="Buscar tecnologia..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          autoFocus
        />
        {visibleTecs.length > 0 ? (
          <div className="bsf-simple-picker-bulk-anchor" ref={bulkAnchorRef}>
            <button
              type="button"
              className="bsf-simple-picker-bulk-trigger"
              aria-label="Mais opções"
              aria-expanded={bulkMenuOpen}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                setBulkMenuOpen((open) => !open);
              }}
            >
              <ListFilter size={12} strokeWidth={2} aria-hidden />
            </button>
            {bulkMenuOpen ? (
              <div className="bsf-simple-picker-bulk-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="bsf-simple-picker-bulk-menu-item"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleBulkToggle}
                >
                  {allVisibleSelected ? 'Desmarcar tudo' : 'Selecionar tudo'}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="bsf-simple-picker-list" role="listbox" aria-label="Tecnologias por grupo">
        {filteredGroups.length > 0 ? (
          filteredGroups.map(({ grupo, itens }) => {
            const allGroupSelected = itens.length > 0 && itens.every((n) => selectedSet.has(n));
            return (
              <div key={grupo} className="bsf-simple-picker-group">
                <button
                  type="button"
                  className={`bsf-simple-picker-item bsf-simple-picker-item--uf-head deq-tec-group-head${
                    allGroupSelected ? ' is-selected' : ''
                  }`}
                  aria-label={`Selecionar todas de ${grupo}`}
                  aria-pressed={allGroupSelected}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onToggleGrupo(grupo, itens);
                  }}
                >
                  <span className="bsf-simple-picker-item-label">{grupo}</span>
                  <span className="bsf-simple-picker-item-meta">{itens.length}</span>
                  <Check size={12} strokeWidth={2.5} className="bsf-simple-picker-check" aria-hidden />
                </button>
                {itens.map((nome) => {
                  const selected = selectedSet.has(nome);
                  return (
                    <button
                      key={`${grupo}-${nome}`}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`bsf-simple-picker-item bsf-simple-picker-item--city${
                        selected ? ' is-selected' : ''
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onToggleTec(nome);
                      }}
                    >
                      <span className="bsf-simple-picker-item-label" title={nome}>
                        {nome}
                      </span>
                      <Check size={12} strokeWidth={2.5} className="bsf-simple-picker-check" aria-hidden />
                    </button>
                  );
                })}
              </div>
            );
          })
        ) : (
          <div className="bsf-simple-picker-empty">
            {searchTerm ? `Nenhum resultado para "${searchTerm}"` : 'Nenhuma tecnologia disponível'}
          </div>
        )}
      </div>
    </BoardSearchPickerShell>
  );
}
